/**
 * Команда бренда.
 *
 * До неё у бренда был ровно один аккаунт — тот, что завела платформа. Кассир,
 * выдающий призы, работал под ним же, то есть мог запускать акции, править
 * условия и грузить коды. В аудит-журнале при этом стояла одна почта на всех,
 * и вопрос «кто это сделал» не имел ответа.
 *
 * Человека зовут той же одноразовой ссылкой, которой платформа зовёт бренд:
 * аккаунт он заводит себе сам, пароля не знает никто — ни бренд, ни мы.
 */
import type { BrandRole, TeamView } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Field, Skeleton, fmtShortDay } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useToast } from '../toast';

const ROLE_NAME: Record<BrandRole, string> = {
  owner: 'Владелец',
  manager: 'Менеджер',
  operator: 'Выдаёт призы',
};

const ROLE_CAN: Record<BrandRole, string> = {
  owner: 'Всё: акции, условия, коды, команда и профиль бренда.',
  manager: 'Акции, коды, розыгрыши и выдача. Профиль бренда и смета — нет.',
  operator: 'Только выдача призов: видит, что выдать, и отмечает выдачу.',
};

export function TeamScreen() {
  const toast = useToast();
  const team = useResource(() => api.consoleTeam(), []);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<BrandRole>('operator');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ссылка показывается один раз: в базе лежит только её отпечаток, и
  // переспросить её нельзя даже нам. Поэтому она висит на экране, пока
  // человек её не скопирует, а не исчезает по таймеру.
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      const issued = await api.consoleInviteMember(email.trim(), role);
      setLink({ email: email.trim(), url: issued.url });
      setEmail('');
      team.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось позвать');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (who: string) => {
    try {
      await api.consoleRemoveMember(who);
      toast('Доступ забран');
      team.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не удалось снять');
    }
  };

  const revoke = async (id: string) => {
    try {
      await api.consoleRevokeMemberInvite(id);
      toast('Приглашение отозвано');
      team.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не удалось отозвать');
    }
  };

  const data: TeamView | null = team.data;
  const mayInvite = (data?.can_invite ?? []).length > 0;

  return (
    <Layout title="Команда" subtitle="кто работает с акциями бренда и кто выдаёт призы">
      {team.loading && <Skeleton count={4} />}

      {data && (
        <>
          <Card>
            <div className="h2" style={{ marginBottom: 10 }}>Сотрудники</div>
            {data.members.length === 0 ? (
              <Empty title="Пока никого" />
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Почта</th>
                    <th>Роль</th>
                    <th>С нами с</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.email}>
                      <td>
                        <div>{m.name || m.email}</div>
                        {m.name && <div className="faint" style={{ fontSize: 12 }}>{m.email}</div>}
                      </td>
                      <td>
                        <Badge tone={m.role === 'owner' ? 'good' : undefined}>
                          {ROLE_NAME[m.role]}
                        </Badge>
                      </td>
                      <td className="faint">{fmtShortDay(m.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {(data.can_invite ?? []).includes(m.role) && (
                          <Button size="sm" variant="danger" onClick={() => void remove(m.email)}>
                            Забрать доступ
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {data.invites.length > 0 && (
            <Card style={{ marginTop: 12 }}>
              <div className="h2" style={{ marginBottom: 6 }}>Ждут по ссылке</div>
              <p className="sub" style={{ marginBottom: 10 }}>
                Человек ещё не завёл себе вход. Ссылка живёт две недели и срабатывает один раз.
              </p>
              <table className="tbl">
                <tbody>
                  {data.invites.map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.email}</td>
                      <td><Badge>{ROLE_NAME[inv.role]}</Badge></td>
                      <td className="faint">до {fmtShortDay(inv.expires_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button size="sm" onClick={() => void revoke(inv.id)}>
                          Отозвать
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {mayInvite && (
            <Card style={{ marginTop: 12 }}>
              <div className="h2" style={{ marginBottom: 6 }}>Позвать человека</div>
              <p className="sub" style={{ marginBottom: 12 }}>
                Он получит ссылку, по которой сам заведёт себе вход и придумает пароль. Пароль не
                узнает никто — ни ты, ни платформа.
              </p>

              <Field label="Рабочая почта" error={error ?? undefined}>
                <input
                  className="input"
                  type="email"
                  placeholder="kassir@brand.tj"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>

              <label className="lab" style={{ marginTop: 10 }}>Роль</label>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {(data.can_invite ?? []).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`chip ${role === r ? 'on' : ''}`}
                    onClick={() => setRole(r)}
                  >
                    {ROLE_NAME[r]}
                  </button>
                ))}
              </div>
              <p className="sub" style={{ fontSize: 11.5, marginTop: 6 }}>{ROLE_CAN[role]}</p>

              <div style={{ marginTop: 12 }}>
                <Button
                  variant="pri"
                  disabled={busy || !email.includes('@')}
                  onClick={() => void invite()}
                >
                  {busy ? 'Готовим ссылку…' : 'Получить ссылку'}
                </Button>
              </div>
            </Card>
          )}

          {link && (
            <Card style={{ marginTop: 12 }}>
              <div className="h2" style={{ marginBottom: 6 }}>Ссылка для {link.email}</div>
              <p className="sub" style={{ marginBottom: 10 }}>
                Покажется один раз: в базе лежит только её отпечаток, и переспросить её нельзя даже
                нам. Потерялась — позови этого человека заново, старая ссылка отзовётся.
              </p>
              <input className="input mono" readOnly value={link.url} onFocus={(e) => e.target.select()} />
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <Button
                  variant="pri"
                  onClick={() => {
                    void navigator.clipboard?.writeText(link.url);
                    toast('Ссылка скопирована');
                  }}
                >
                  Скопировать
                </Button>
                <Button onClick={() => setLink(null)}>Я передал</Button>
              </div>
            </Card>
          )}
        </>
      )}
    </Layout>
  );
}
